import { memo } from 'react';
import { Box } from '@mui/material';
import { Event } from '../../types';
import { getClientName, getHasWarnings } from '../../utils';
import { Chip } from './Chip';
import { useSelector } from 'hooks';

type Props = Event & {
  mousePosition: { x: number; y: number };
  selected?: boolean;
  width?: number;
  opacity?: number;
  isDroppedOverlay?: boolean;
};

export const AppointmentChipOverlay = memo((props: Props) => {
  const {
    id,
    activity,
    appointmentGrouping,
    clients,
    employees,
    isRecurring,
    isAbsent,
    mousePosition = { x: 0, y: 0 },
    warnings: {
      startsTooEarly,
      startsTooLate,
      excludedEmployeeClientCombination,
      isInSync: isInSyncWithEcd = true,
      hasOverlap
    },
    timeWindow,
    width,
    selected,
    opacity,
    isDroppedOverlay = false
  } = props;
  const client = clients[0];
  const isClient = activity?.clientRelatedType !== 'NON_CLIENT_RELATED';
  const clientName = getClientName(client) ?? 'Onbekend';
  const { defaultZoomLevel } = useSelector((state) => state.preferences);

  const hasWarnings = getHasWarnings({
    startsTooEarly,
    startsTooLate,
    isInSyncWithEcd,
    excludedEmployeeClientCombination,
    isUrgent: !!timeWindow?.isUrgent,
    hasOverlap
  });

  const rect = document.getElementById(id)?.getBoundingClientRect() || {
    right: 0,
    bottom: 0,
    width: 0,
    height: 0
  };

  // Check if chip is still roughly the same size as the original chip (within 5px)
  // Note: This is for the fact that the chip is not always the same size as the original chip when dragging has initiated
  const hasTransformedSize = Math.abs((width || 0) - rect.width) > 10;
  const isToPlan =
    !appointmentGrouping && (!employees || employees?.length === 0); // Only to plan appointments transform from card to chip

  const offsetX = mousePosition.x - rect.right + rect.width;
  const offsetY = mousePosition.y - rect.bottom + rect.height;

  return (
    <Box
      id={id}
      sx={{
        opacity,
        width,
        transform:
          hasTransformedSize && isToPlan
            ? `translate(${isDroppedOverlay ? 0 : offsetX}px, ${
                isDroppedOverlay ? 0 : offsetY
              }px)`
            : 'initial'
      }}
    >
      <Chip
        width={width as number}
        isAbsent={isAbsent}
        isRecurring={isRecurring}
        isClient={isClient}
        selected={!!selected}
        hasWarnings={hasWarnings}
        handleRightClick={() => ({})}
        detailZoomLevel={defaultZoomLevel}
      >
        <Box
          sx={{
            whiteSpace: 'nowrap',
            fontSize: '12px',
            height: '18px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textDecoration: isAbsent ? 'line-through' : 'none'
          }}
        >
          {isClient ? clientName : activity?.name}
        </Box>
      </Chip>
    </Box>
  );
});
